import { useEffect, useState } from "react";

import { Plaque } from "./Plaque";
import type { KothRoundState } from "./useKothRound";
import { useTouchActivity, type TouchEntry } from "./useTouchActivity";

/**
 * Wall-mounted live feed of *real* on-chain touches — "7cQ..k9  2m ago".
 *
 * Honest social proof in place of a fake crowd: every line is a touch that
 * actually landed on-chain (see `useTouchActivity`), so an empty room shows a
 * "be the first" line rather than fabricated players. Mirrors
 * `RoundTimerPlaque` / `ChatMirrorBoard`: read state → format lines → render via
 * the shared `Plaque`.
 */

const MAX_LINES = 6;

interface RecentActivityPlaqueProps {
  round: KothRoundState | null;
  position: [number, number, number];
  rotation?: [number, number, number];
}

// Local copy mirrors the sibling plaques (KingNameplate, KothThroneStation each
// keep their own short-pubkey helper) rather than refactoring 8 call sites.
function shortPk(pk: string): string {
  return pk.length <= 8 ? pk : `${pk.slice(0, 4)}…${pk.slice(-4)}`;
}

function lamportsToSol(lamports: bigint): string {
  return (Number(lamports) / 1e9).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Relative time since *local* observation. Safe to use Date.now here — we are
 *  measuring elapsed wall-clock on this machine, not comparing to chain time. */
function ago(seenAtMs: number, nowMs: number): string {
  const s = Math.max(0, Math.floor((nowMs - seenAtMs) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function formatLine(e: TouchEntry, nowMs: number): string {
  const who = shortPk(e.toucher);
  // Ring-seeded entries carry no timestamp/pot — show just who took the throne.
  if (e.seenAtMs == null) return `${who}  took the throne`;
  const pot = e.potLamports != null ? `  ·  ${lamportsToSol(e.potLamports)} SOL` : "";
  return `${who}  ${ago(e.seenAtMs, nowMs)}${pot}`;
}

export function RecentActivityPlaque({
  round,
  position,
  rotation,
}: RecentActivityPlaqueProps): JSX.Element {
  const entries = useTouchActivity(round);

  // 1 Hz re-render so the "Ns / Nm ago" labels stay fresh, matching the
  // setInterval cadence KothHud uses for its countdown badges. One cheap tick.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const lines =
    entries.length === 0
      ? ["No touches yet —", "be the first 👑"]
      : entries.slice(0, MAX_LINES).map((e) => formatLine(e, nowMs));

  return (
    <Plaque
      position={position}
      rotation={rotation}
      width={3.2}
      height={3.0}
      backerColor="#1a1410"
      title="RECENT ACTIVITY"
      titleColor="#ffd66b"
      titleSize={0.3}
      lines={lines}
      bodyColor="#f4ecd8"
      bodySize={0.26}
      align="left"
    />
  );
}
